import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Translate } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import OverflowingText from '../../components/foundations/OverflowingText';
import Tooltip from '../../components/molecules/Tooltip';
import PermissionsWrapper from '../../containers/PermissionsWrapper';
import AndroidIcon from '../../resources/icons/os/android.svg';
import AppleIcon from '../../resources/icons/os/ios.svg';
import PCConsoleIcon from '../../resources/icons/os/pc_console.svg';
import { getOrgAgencies } from '../../selectors/agencies';
import { getAttributionAppsList } from '../../selectors/apps';
import { getReadOnly } from '../../selectors/permissions';
import { getSingularLinksSourcesList } from '../../selectors/sources';
import { getIsAgency, isUserAdNetwork } from '../../selectors/user';
import { checkAgencyPermissionsForLinkSites } from '../../utils/agencyPermissions';
import { TOGGLE_TAB_GROUP_OPTIONS } from '../consts';
import { LINK_TYPE_LEGACY, LINK_TYPE_MOBILE_PARTNER, LINK_TYPE_MOBILE_WEB_TO_APP } from '../linkTypes';
import { getSelectedToggleTab } from '../selectors';
import { extractLinkAppSitesFromLinkData } from '../utils';
import css from './LinkRowItem.css';

const platformToIcon = {
    android: AndroidIcon,
    ios: AppleIcon,
    pcConsole: PCConsoleIcon,
};

const iconProps = {
    size: IconSizes.SMALL,
    variant: IconVariants.LIGHT,
};

const LinkRowItem = ({
    type,
    linksCount,
    agency_name: agencyName = '',
    agency_id: agencyId = '',
    campaign_name: campaignName = '',
    tag_id: tagId = '',
    singular_link: singularLink = false,
    configured = {},
    createdDisplay = '',
    modifiedDisplay = '',
    onEditClick = (tagId, isLegacyLink) => {},
    onViewClick = () => {},
    absolute_link_type: absoluteLinkType,
    platform,
    longname,
    partner = '',
    ...props
}) => {
    const readOnly = useSelector(getReadOnly);
    const selectedToggleTab = useSelector(state => getSelectedToggleTab(state));
    const isAgency = useSelector(getIsAgency);
    const isPartner = useSelector(isUserAdNetwork);
    const orgAgencies = useSelector(getOrgAgencies);
    const hasAgencies = orgAgencies?.length > 0;

    const appsList = useSelector(getAttributionAppsList);
    const sourcesList = useSelector(getSingularLinksSourcesList);

    // Show agency for advertisers with agencies OR for partners
    const showAgency =
        (hasAgencies || isPartner) &&
        [TOGGLE_TAB_GROUP_OPTIONS.AGENCY_LINKS, TOGGLE_TAB_GROUP_OPTIONS.ALL_LINKS].includes(selectedToggleTab);

    // Get internal source name from display name
    // For partner links, the partner field contains display_name
    // For custom links, the partner field contains the internal name
    const internalSourceName = useMemo(() => {
        if (!partner || !sourcesList?.length) {
            return partner;
        }
        const isPartnerLink = type === LINK_TYPE_MOBILE_PARTNER;
        if (isPartnerLink) {
            // For partner links, look up by display_name to get the internal name
            const source = sourcesList.find(s => s.display_name === partner);
            return source?.name || partner;
        }
        // For custom links, the partner field is already the internal name
        return partner;
    }, [partner, sourcesList, type]);

    const agencyHasPermissions = useMemo(() => {
        if (!agencyId || isAgency || !orgAgencies || !appsList) {
            return true;
        }

        // Extract app sites from link data
        const linkAppSites = extractLinkAppSitesFromLinkData({ configured, platform, longname }, appsList);
        if (!linkAppSites.length) {
            return false;
        }

        return checkAgencyPermissionsForLinkSites(orgAgencies, agencyId, linkAppSites, internalSourceName);
    }, [agencyId, isAgency, orgAgencies, appsList, platform, longname, configured, internalSourceName]);

    // Show view icon if:
    // 1. User has readonly permission (e.g., partner), OR
    // 2. Advertiser viewing agency link without permissions
    const showViewIcon = readOnly || (agencyId && !isAgency && !agencyHasPermissions);

    const isLegacyLink = absoluteLinkType === LINK_TYPE_LEGACY;
    const tooltipProps = {
        distance: linksCount > 1 ? 10 : 30,
    };

    const actionWrapper = (onClick, ...props) => e => {
        e.stopPropagation();
        onClick(...props);
    };

    const onEditClickHandler = () => {
        onEditClick(tagId, isLegacyLink);
    };

    const onDuplicateClicked = () => {
        props.onDuplicateClicked(tagId, readOnly);
    };

    const onArchiveClicked = () => {
        props.onArchiveClicked(tagId);
    };

    return (
        <div className={classNames(css.container, { [css.agencyRow]: agencyId && !isAgency })}>
            <OverflowingText className={css.campaignName}>{campaignName}</OverflowingText>
            {showAgency && (
                <div className={css.icons}>
                    <div className={css.agency}>
                        {(agencyName || agencyId) && (
                            <>
                                <Icon
                                    name="briefcase"
                                    size={IconSizes.X_SMALL}
                                    variant={IconVariants.LIGHT}
                                    interactive={false}
                                />
                                <OverflowingText>
                                    {agencyName && agencyId ? `${agencyName} (${agencyId})` : agencyName || agencyId}
                                </OverflowingText>
                            </>
                        )}
                    </div>
                </div>
            )}
            <div className={css.icons}>
                {Object.entries(configured).map(([platform, isConfigured]) => {
                    const IconElement = platformToIcon?.[platform];
                    return isConfigured && IconElement ? (
                        <IconElement key={platform} className={css.icon} />
                    ) : (
                        <div key={platform} className={classNames(css.icon, css.noIcon)} />
                    );
                })}
            </div>
            <div className={css.dates}>
                {createdDisplay && (
                    <div>
                        <Translate id="STATIC.PAGES.MANAGE_LINKS.LINK_ROW_ITEM_CREATED_AT" data={{ createdDisplay }} />
                    </div>
                )}

                {modifiedDisplay && (
                    <>
                        <div className={css.separator} />
                        <div>
                            <Translate
                                id="STATIC.PAGES.MANAGE_LINKS.LINK_ROW_ITEM_UPDATED_AT"
                                data={{ modifiedDisplay }}
                            />
                        </div>
                    </>
                )}
                {!singularLink ? <div>(Legacy Link)</div> : null}
            </div>
            <div className={css.overlayButtons}>
                {showViewIcon ? (
                    <Tooltip titleTranslationKey="STATIC.TOOLTIPS.VIEW" {...tooltipProps}>
                        <Icon name="eye" {...iconProps} onClick={actionWrapper(onViewClick, tagId, isLegacyLink)} />
                    </Tooltip>
                ) : (
                    <Tooltip titleTranslationKey="STATIC.TOOLTIPS.EDIT" {...tooltipProps}>
                        <Icon name="pencil" {...iconProps} onClick={actionWrapper(onEditClickHandler)} />
                    </Tooltip>
                )}
                <PermissionsWrapper write>
                    {!showViewIcon && singularLink && type !== LINK_TYPE_MOBILE_WEB_TO_APP ? (
                        <Tooltip titleTranslationKey="STATIC.TOOLTIPS.DUPLICATE" {...tooltipProps}>
                            <Icon name="copy" {...iconProps} onClick={actionWrapper(onDuplicateClicked)} />
                        </Tooltip>
                    ) : null}
                    <Tooltip titleTranslationKey="STATIC.TOOLTIPS.ARCHIVE" {...tooltipProps}>
                        <Icon name="archive" {...iconProps} onClick={actionWrapper(onArchiveClicked)} />
                    </Tooltip>
                </PermissionsWrapper>
            </div>
        </div>
    );
};

LinkRowItem.propTypes = {
    type: PropTypes.string,
    agency_name: PropTypes.string,
    agency_id: PropTypes.string,
    campaign_name: PropTypes.string,
    configured: PropTypes.objectOf(PropTypes.any),
    onEditClick: PropTypes.func,
    onViewClick: PropTypes.func,
    onDuplicateClicked: PropTypes.func,
    onArchiveClicked: PropTypes.func,
    createdDisplay: PropTypes.string,
    absolute_link_type: PropTypes.string,
    modifiedDisplay: PropTypes.string,
    singular_link: PropTypes.bool,
    tag_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    campaign_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    linksCount: PropTypes.number,
    platform: PropTypes.string,
    longname: PropTypes.string,
    partner: PropTypes.string,
};

export default LinkRowItem;
